/*
 * Copyright (c) 2026. Bolmate B.V.
 */
import './style/dashboardPage.scss';

import React from 'react';

import {useTranslation} from 'react-i18next';

import IDate from '@dvrd/idate';

import {BolmateEmptyState, BolmatePeriodSelector, BolmateSegmentedToggle} from '../controls';

import PortalSelectController from '../controls/portalSelect/portalSelectController';

import {PeriodLabel} from '../../util/enums';

import {OverviewPeriodRange} from '../../util/interfaces';

import DashboardKpiController from './kpi/dashboardKpiController';

import AdRevenueController from './sections/adRevenue/adRevenueController';

import BuyBoxController from './sections/buyBox/buyBoxController';

import LowStockController from './sections/lowStock/lowStockController';

import ReturnsController from './sections/returns/returnsController';

import ReviewsController from './sections/reviews/reviewsController';

import TopProductsController from './sections/topProducts/topProductsController';

import StatusBannerController from './statusBanner/statusBannerController';
import {PRESET_PERIODS} from '../../util/dateUtil.ts';
import {PeriodRangeFunc} from '../../util/types.ts';

interface Props {
    firstName: string;
    period: OverviewPeriodRange;
    periodRange: [IDate, IDate];
    periodPreset: PeriodLabel | null;
    comparisonLabel: string;
    hideComparison: boolean;
    onChangePeriod: (range: [IDate, IDate], preset: PeriodLabel | null) => void;
    onChangeQuickPeriod: (preset: PeriodLabel) => void;
    accountOptions: Array<{value: string; label: string}>;
    accountIds: Array<string>;
    onChangeAccounts: (value: string | null, selected: boolean, values?: Array<string>) => void;
}

export default function DashboardPage(props: Props) {
    const {
        firstName,
        period,
        periodRange,
        periodPreset,
        comparisonLabel,
        hideComparison,
        onChangePeriod,
        onChangeQuickPeriod,
        accountOptions,
        accountIds,
        onChangeAccounts
    } = props;
    const {t} = useTranslation();

    // The three periods of the design sit in the toggle; the dots menu next to
    // it holds everything else, so no period appears in both.
    const quickOptions = [
        {value: PeriodLabel.YESTERDAY, label: t('overview.period.yesterday', 'Gisteren')},
        {value: PeriodLabel.LAST_7_DAYS, label: t('overview.period.week', 'Week')},
        {value: PeriodLabel.LAST_30_DAYS, label: t('overview.period.month', 'Maand')}
    ];
    const menuPresets: Record<string, PeriodRangeFunc> = {...PRESET_PERIODS};
    delete menuPresets[PeriodLabel.YESTERDAY];
    delete menuPresets[PeriodLabel.LAST_7_DAYS];
    delete menuPresets[PeriodLabel.LAST_30_DAYS];

    const quickValues = quickOptions.map(option => option.value);
    // While the menu owns the selection none of the segments may look active.
    const quickValue = periodPreset && quickValues.includes(periodPreset) ? periodPreset : ('' as PeriodLabel);

    function accountsLabel() {
        if (!accountIds.length) return t('overview.accounts.none', 'Geen winkel geselecteerd');
        if (accountIds.length === accountOptions.length) return t('overview.accounts.all', 'Alle winkels samen');
        if (accountIds.length === 1) {
            const selected = accountOptions.find(option => option.value === accountIds[0]);
            if (selected) return selected.label;
        }
        return t('overview.accounts.count', '{{count}} winkels', {count: accountIds.length});
    }

    function renderWelcome() {
        return (
            <div className='dashboard-welcome'>
                <h1 className='welcome-title'>
                    {firstName
                        ? t('overview.welcomeNamed', 'Welkom terug, {{name}}! 👋', {name: firstName})
                        : t('overview.welcome', 'Welkom terug! 👋')}
                </h1>
                <label className='welcome-subtitle'>
                    {t('overview.welcomeSubtitle', 'Hieronder het overzicht van je verkopen en prestaties')}
                </label>
                <div className='welcome-controls'>
                    <div className='period-controls'>
                        <BolmateSegmentedToggle
                            className='period-toggle'
                            options={quickOptions}
                            value={quickValue}
                            onChange={onChangeQuickPeriod}
                            fullWidth
                            shape='square'
                        />
                        <BolmatePeriodSelector
                            className='period-menu'
                            variant='menu'
                            value={periodRange}
                            selectedPreset={periodPreset}
                            presets={menuPresets}
                            onChange={onChangePeriod}
                            max={IDate.now().endOf('day')}
                        />
                    </div>
                    <PortalSelectController
                        className='accounts-select'
                        items={accountOptions}
                        value={accountIds}
                        onChange={onChangeAccounts}
                        selectLabel={accountsLabel()}
                        multiple
                        controls={['selectAll', 'deselectAll']}
                        shape='square'
                        size='medium'
                    />
                </div>
            </div>
        );
    }

    function renderSections() {
        return (
            <div className='dashboard-sections'>
                <TopProductsController accountIds={accountIds} period={period} />
                <ReturnsController accountIds={accountIds} period={period} />
                <ReviewsController accountIds={accountIds} period={period} />
                <BuyBoxController accountIds={accountIds} period={period} />
                <LowStockController accountIds={accountIds} />
                <AdRevenueController accountIds={accountIds} period={period} />
            </div>
        );
    }

    // Nothing is scoped to an account, so every section would sit on its own
    // "nothing here" state — say once what the user has to do instead.
    if (!accountIds.length)
        return (
            <div className='dashboard-page'>
                <StatusBannerController />
                {renderWelcome()}
                <BolmateEmptyState
                    className='dashboard-no-accounts'
                    image='empty-dashboard'
                    title={t('overview.accounts.emptyTitle', 'Geen winkel geselecteerd')}
                >
                    {t('overview.accounts.emptyBody', 'Kies hierboven minimaal één winkel om je cijfers te zien.')}
                </BolmateEmptyState>
            </div>
        );

    return (
        <div className='dashboard-page'>
            <StatusBannerController />
            {renderWelcome()}
            <DashboardKpiController
                accountIds={accountIds}
                period={period}
                comparisonLabel={comparisonLabel}
                hideComparison={hideComparison}
            />
            {renderSections()}
        </div>
    );
}
